/* eslint-disable no-param-reassign */
import { forEach, isEmpty, map } from 'lodash';

import addLayerToCesiumViewer from './addLayerToCesiumViewer';
import '../leaflet-operations/customLayerControl';

const FULFILLED_STATUS = 'fulfilled';

/**
 * Adds the selected basemaps for the map.
 *
 * @async
 * @param {object} arg - The object argument.
 * @param {object} arg.baseMaps - The base maps to draw in the map.
 * @param {object} arg.baseMapsToDisplay - The leaflet layer that contains the base maps in the map.
 * @param {Array} arg.baseMapsTransparency - An array containing the base maps that need a different transparency value.
 * @param {Array} arg.baseMapsZoom - An array containing the base maps that need a different zoom value.
 * @param {object} arg.cesiumViewer - The leaflet map object.
 * @param {boolean} [arg.isUpdate=false] - Flag to determine if we are doing an update.
 * @param {object} arg.layers - The layers to draw in the map.
 * @param {boolean} [arg.layersOverlayed=true] - Flag to determine if layers are overlayed in the map.
 * @param {object} arg.layersToDisplay - The leaflet layer that contains the layers in the map.
 * @param {Array} arg.layersTransparency - An array containing the layers that need a different transparency value.
 * @param {Array} arg.layersZoom - An array containing the layers that need a different zoom value.
 */
const drawViewerLayers = async ({
  baseMaps,
  baseMapsToDisplay,
  baseMapsTransparency,
  cesiumViewer,
  isUpdate = false,
  layers,
  layersOverlayed = true,
  layersToDisplay,
  layersTransparency
}) => {
  const newLayersToDraw = {};
  const newBaseMapsToDraw = {};

  if (isUpdate && !isEmpty(baseMapsToDisplay))
    forEach(baseMapsToDisplay, (layer, layerName) => {
      try {
        cesiumViewer.imageryLayers.remove(baseMapsToDisplay[layerName]);
      } catch (error) {
        console.error(error);
      }
    });

  if (isUpdate && !isEmpty(layersToDisplay))
    forEach(layersToDisplay, (layer, layerName) => {
      try {
        cesiumViewer.dataSources.remove(layersToDisplay[layerName]);
      } catch (error) {
        console.error(error);
      }
    });

  const baseMapsPromises = map(baseMaps, baseMap =>
    addLayerToCesiumViewer({
      cesiumViewer,
      layer: baseMap,
      layersTransparency: baseMapsTransparency
    })
  );
  const baseMapsArray = await Promise.allSettled(baseMapsPromises);

  forEach(baseMapsArray, (baseMap, index) => {
    if (baseMap.status === FULFILLED_STATUS)
      newBaseMapsToDraw[baseMaps[index].name] = baseMap.value;
    else console.error(baseMap.reason);
  });

  const layersPromises = map(layers, (layer, index) =>
    addLayerToCesiumViewer({
      cesiumViewer,
      isAdded: index === 0 || layersOverlayed,
      layer,
      layersTransparency
    })
  );
  const layersArray = await Promise.allSettled(layersPromises);

  forEach(layersArray, (layer, index) => {
    if (layer.status === FULFILLED_STATUS) newLayersToDraw[layers[index].name] = layer.value;
    else console.error(layer.reason);
  });

  return { newLayersToDraw, newBaseMapsToDraw };
};

export default drawViewerLayers;
